import * as React from 'react'
import { useCallback, useState } from 'react'
import {
  cancelForm,
  deleteForm,
  getAgreementConfigs,
  reviseForm,
} from '@user/store/agreements/api'
import { EmbedFormManagerDrawer } from '@user/components/Agreements/EmbedFormManagerDrawer/EmbedFormManagerDrawer'
import { FM_FORM_STATUSES } from '@user/store/agreements/typings'
import { FormActions } from '@user/components/Agreements/EmbedFormManagerDrawer/typings'
import { ENTITY_TYPES, getBlobObjectURL } from '@sales-center/common'
import {
  AlertDialogSlide,
  MoreButton as MoreButtonAtom,
  toast,
} from '@sales-center/components'
import { ServiceConfig } from '@user/store/services/config'
import { getAuthHeader } from '@user/store/services/crud'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { updateLeadAgreementsCount } from '@user/store/lead/slice'
import { updateOpportunityAgreementsCount } from '@user/store/opportunity/slice'
import { useDialogState } from '@user/utils/useDialogState'
import { useFormPacketsEnabled } from '@user/store/tenantFeatures/hooks/useFormPacketsEnabled'

interface Props {
  entityId: number
  entityType: ENTITY_TYPES
  id: number
  onUpdateStatus?: () => void
  openAgreementCreateDrawer?: () => void
  status: FM_FORM_STATUSES
  canUpdate: boolean
}

export const AgreementActionsButton = (props: Props) => {
  const {
    entityId,
    entityType,
    id,
    status,
    onUpdateStatus,
    canUpdate,
    openAgreementCreateDrawer,
  } = props
  const baseUrl = `${ServiceConfig.baseAPIUrl}/Proposals`
  const dispatch = useAppDispatch()
  const formPacketEnabled = useFormPacketsEnabled()
  const [action, setAction] = useState<FormActions>(FormActions.Edit)
  const [embedDrawerOpen, setEmbedDrawerOpen] = useState<boolean>(false)
  const [reviseDialogOpen, showReviseDialog, hideReviseDialog] =
    useDialogState()
  const [cancelDialogOpen, showCancelDialog, hideCancelDialog] =
    useDialogState()
  const [deleteDialogOpen, showDeleteDialog, hideDeleteDialog] =
    useDialogState()
  const [ongoingDeletion, setOngoingDeletion] = useState<boolean>(false)
  const [ongoingRevision, setOngoingRevision] = useState<boolean>(false)

  const handleReviseAgreement = useCallback(async () => {
    setOngoingRevision(true)
    const response = await dispatch(reviseForm(id))
    if (reviseForm.fulfilled.match(response)) {
      if (onUpdateStatus) {
        onUpdateStatus()
      }
      hideReviseDialog()
      toast.success(`Agreement status updated.`)
      if (formPacketEnabled && openAgreementCreateDrawer) {
        const configResponse = await dispatch(getAgreementConfigs(id))
        if (getAgreementConfigs.fulfilled.match(configResponse)) {
          openAgreementCreateDrawer()
        } else {
          toast.error(`Failed to get agreement configs.`)
        }
      } else {
        setAction(FormActions.Edit)
        setEmbedDrawerOpen(true)
      }
    } else {
      toast.error(`Failed to revise agreement.`)
    }
    setOngoingRevision(false)
  }, [
    dispatch,
    formPacketEnabled,
    hideReviseDialog,
    id,
    onUpdateStatus,
    openAgreementCreateDrawer,
  ])

  const handleCancelAgreement = useCallback(async () => {
    const response = await dispatch(cancelForm(id))

    if (cancelForm.fulfilled.match(response)) {
      if (onUpdateStatus) {
        onUpdateStatus()
      }
      hideCancelDialog()
      toast.success(`Agreement cancelled.`)
    } else {
      toast.error(`Failed to cancel agreement.`)
    }
  }, [dispatch, hideCancelDialog, id, onUpdateStatus])

  const handleDeleteAgreement = useCallback(async () => {
    setOngoingDeletion(true)
    const response = await dispatch(deleteForm(id))

    if (deleteForm.fulfilled.match(response)) {
      if (entityType == ENTITY_TYPES.Lead) {
        dispatch(
          updateLeadAgreementsCount({
            action: 'decrement',
            leadId: entityId,
          }),
        )
      }

      if (entityType == ENTITY_TYPES.Opportunity) {
        dispatch(
          updateOpportunityAgreementsCount({
            action: 'decrement',
            oppId: entityId,
          }),
        )
      }

      if (onUpdateStatus) {
        onUpdateStatus()
      }
      hideDeleteDialog()
      toast.success(`Agreement deleted.`)
    } else {
      toast.error(`Failed to delete agreement.`)
    }
    setOngoingDeletion(false)
  }, [
    dispatch,
    entityId,
    entityType,
    hideDeleteDialog,
    id,
    onUpdateStatus,
    setOngoingDeletion,
  ])

  const handleUpdateAgreement = useCallback(async () => {
    const response = await dispatch(getAgreementConfigs(id))

    if (getAgreementConfigs.fulfilled.match(response)) {
      if (openAgreementCreateDrawer) {
        openAgreementCreateDrawer()
      }
    } else {
      toast.error(`Failed to get agreement configs.`)
    }
  }, [dispatch, id, openAgreementCreateDrawer])

  return (
    <>
      <MoreButtonAtom
        dataTestId={`agreement-actions-button-${id}`}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        options={[
          {
            label: 'Cancel',
            hidden:
              !canUpdate ||
              (status != FM_FORM_STATUSES.Complete &&
                status != FM_FORM_STATUSES.OutForSignature),
            onClick: showCancelDialog,
          },
          {
            label: 'Edit Form',
            hidden:
              !canUpdate ||
              status == FM_FORM_STATUSES.Canceled ||
              status == FM_FORM_STATUSES.Complete ||
              status == FM_FORM_STATUSES.OutForSignature,
            onClick: async () => {
              setAction(FormActions.Edit)
              setEmbedDrawerOpen(true)
            },
          },
          {
            label: 'Revise',
            hidden:
              !canUpdate ||
              (status != FM_FORM_STATUSES.Complete &&
                status != FM_FORM_STATUSES.OutForSignature),
            onClick: showReviseDialog,
          },
          {
            label: `Send Notification Link`,
            hidden:
              status == FM_FORM_STATUSES.Canceled ||
              status == FM_FORM_STATUSES.Complete,
            onClick: async () => {
              setAction(FormActions.Notify)
              setEmbedDrawerOpen(true)
            },
          },
          {
            label: `View / Download as a PDF`,
            onClick: () => {
              getBlobObjectURL(`${baseUrl}/form/${id}/pdf`, getAuthHeader)
                .then((url) => window.open(url))
                .catch(() => {
                  toast.error(`Failed to download agreement.`)
                })
            },
          },
          {
            label: 'Delete',
            hidden:
              !canUpdate ||
              status == FM_FORM_STATUSES.Complete ||
              status == FM_FORM_STATUSES.OutForSignature ||
              status == FM_FORM_STATUSES.Signed,
            onClick: showDeleteDialog,
          },
          {
            label: 'Update Agreement',
            hidden:
              !formPacketEnabled ||
              !openAgreementCreateDrawer ||
              !canUpdate ||
              (status != FM_FORM_STATUSES.Draft &&
                status != FM_FORM_STATUSES.InProgress &&
                status != FM_FORM_STATUSES.Declined),
            onClick: handleUpdateAgreement,
          },
        ]}
      />

      <EmbedFormManagerDrawer
        action={action}
        formId={id}
        open={embedDrawerOpen}
        onClose={() => {
          if (onUpdateStatus) {
            onUpdateStatus()
          }
          setEmbedDrawerOpen(false)
        }}
      />

      {/* ========== R E V I S E   C O N F I R M   P O P U P ========== */}
      <AlertDialogSlide
        cancelClick={hideReviseDialog}
        message="Are you sure you want to revise this agreement?"
        okButtonText="Revise"
        okClick={handleReviseAgreement}
        open={reviseDialogOpen}
        title="Revise Agreement"
        okButtonDisabled={ongoingRevision}
      />

      {/* ========== C A N C E L   C O N F I R M   P O P U P ========== */}
      <AlertDialogSlide
        cancelClick={hideCancelDialog}
        message="Are you sure you want to cancel this agreement?"
        okButtonText="Yes"
        cancelButtonText="No"
        okClick={handleCancelAgreement}
        open={cancelDialogOpen}
        title="Cancel Agreement"
      />

      {/* ========== C A N C E L   C O N F I R M   P O P U P ========== */}
      <AlertDialogSlide
        cancelClick={hideDeleteDialog}
        message="Are you sure you want to delete this agreement?"
        okButtonText="Delete"
        okClick={handleDeleteAgreement}
        open={deleteDialogOpen}
        title="Delete Agreement"
        okButtonDisabled={ongoingDeletion}
      />
    </>
  )
}

AgreementActionsButton.displayName = 'AgreementActionsButton'
